/**
 * Compact credit balance + plan in the app bar, linking to Billing.
 *
 * Before this the only way to learn your balance was to navigate to
 * Admin › Billing and know it existed — onboarding never mentions
 * billing at all — so an org's first signal that credits were running
 * out was a turn being refused mid-session.
 *
 * Manager-only and flag-gated: the queries behind it are org-admin
 * scoped, and an empty slug disables them, so a plain member (and every
 * billing-off deployment) issues no requests at all. Only a viewer who
 * may open Billing gets the link (and the plan, which only they may
 * read); a manager sees the balance and whom to ask.
 */
import * as React from "react";
import { Chip, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useOrgBilling } from "@/api/billing";
import { Credits as CreditsGlyph } from "@/components/icons";
import { useMe, useOrgLedger } from "@/api/hooks";
import { intlLocale } from "@/i18n/format";

import { canOpenBilling } from "./billingAccess";
import { resolveBillingStatus } from "./billingStatus";

const TONE_COLOR = {
  ok: "default",
  low: "warning",
  empty: "error",
} as const;

export function BillingStatusChip(): React.JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useMe();

  const billingEnabled = Boolean(me.data?.features?.billing_enabled);
  const canManage = Boolean(me.data?.admin_scope?.can_access_admin);
  const slug = me.data?.admin_scope?.active_org_slug ?? null;
  const gate = billingEnabled && canManage ? (slug ?? "") : "";
  const opensBilling = canOpenBilling(me.data, slug);

  const ledger = useOrgLedger(gate);
  const billing = useOrgBilling(opensBilling ? gate : "");

  if (!gate) return null;

  const status = resolveBillingStatus({
    balanceMicros: ledger.data?.balance_micros,
    microsPerCredit: me.data?.credits?.micros_per_credit ?? 10_000,
    locale: intlLocale(),
    planName: billing.data?.agreement?.plan_name ?? null,
    // The server already folds the global flag, the per-org override
    // and the negative cap into below_cap — re-deriving it here would
    // be a second, drifting copy of the block rule.
    blocked: Boolean(ledger.data?.below_cap),
    enforced: Boolean(me.data?.credits?.enforced),
    loading: ledger.isLoading || billing.isLoading,
  });
  if (!status.show) return null;

  const empty = status.tone === "empty";
  let title: string;
  if (opensBilling) {
    title = empty
      ? t("billing.status_empty", "Out of credits — open Billing to top up")
      : t("billing.status_tooltip", "Credit balance — open Billing");
  } else {
    // Only an organisation admin may open Billing: say whom to ask.
    title = empty
      ? t("billing.status_empty_ask_admin")
      : t("billing.status_aria", "Credit balance");
  }

  return (
    <Tooltip title={title}>
      <Chip
        size="small"
        color={TONE_COLOR[status.tone]}
        variant={status.tone === "ok" ? "outlined" : "filled"}
        // The mark turns a bare number in a crowded app bar into a
        // credit figure. Without it "0" beside a language selector and a
        // notification count is just another badge.
        icon={<CreditsGlyph sx={{ fontSize: "0.95rem" }} />}
        label={status.label}
        onClick={opensBilling ? () => navigate("/app/admin/billing") : undefined}
        // A chip that opens nothing is no button: a named group, whose
        // name says what its tooltip says. (An explicit undefined role
        // would drop the button role a clickable chip has.)
        {...(opensBilling ? {} : { role: "group" })}
        aria-label={opensBilling ? t("billing.status_aria", "Credit balance") : title}
        sx={{ mr: 0.5 }}
      />
    </Tooltip>
  );
}
